import { faCircleQuestion } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Col, Input, Modal, Row } from '@orobix/orobix-fe-components'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { v4 as uuidv4 } from 'uuid'

import { AnnotationType } from '../../types/annotation'
import useSettings from '../Settings/useSettings'
import useUpdateSettings from '../Settings/useUpdateSettings'
import useStyles from './Annotator.styles'

interface AnnotatorInfoModalProps {
  annotationType: AnnotationType
}

const AnnotatorInfoModal = ({ annotationType }: AnnotatorInfoModalProps) => {
  const { t } = useTranslation()
  const { settings, isFetching } = useSettings()
  const { updateSettings, inProgress } = useUpdateSettings()
  const [isOpen, setOpen] = useState(false)
  const [isCheck, setCheck] = useState(false)
  const classes = useStyles()
  const FADE_TIMER = 4000
  const [isFading, setFading] = useState(false)

  const annotationTypeToInfoList = (): string[] => {
    let instructions: string[] = []
    switch (annotationType) {
      case 'Tag':
        instructions = [t('ANNOTATOR_INFO_TAG_1'), t('ANNOTATOR_INFO_TAG_2')]
        break
      case 'Brush':
        instructions = [
          t('ANNOTATOR_INFO_BRUSH_1'),
          t('ANNOTATOR_INFO_BRUSH_2'),
          t('ANNOTATOR_INFO_BRUSH_3'),
          t('ANNOTATOR_INFO_BRUSH_4'),
          t('ANNOTATOR_INFO_BRUSH_5'),
          t('ANNOTATOR_INFO_BRUSH_6'),
        ]
        break
      case 'Polyline':
        instructions = [
          t('ANNOTATOR_INFO_POLYLINE_1'),
          t('ANNOTATOR_INFO_POLYLINE_2'),
          t('ANNOTATOR_INFO_POLYLINE_3'),
          t('ANNOTATOR_INFO_POLYLINE_4'),
          t('ANNOTATOR_INFO_POLYLINE_5'),
          t('ANNOTATOR_INFO_POLYLINE_6'),
        ]
        break
      default:
        break
    }
    instructions.push(t('ANNOTATOR_INFO_END_1'))
    return instructions
  }

  useEffect(() => {
    if (settings?.preference__show_annotator_instructions) {
      setOpen(true)
      setCheck(false)
    } else {
      setCheck(true)
    }
  }, [settings])

  useEffect(() => {
    if (isFading) {
      setTimeout(() => {
        setFading(false)
      }, FADE_TIMER)
    }
  }, [isFading])

  return (
    <>
      <Button
        label={t('How to Use')}
        icon={
          <FontAwesomeIcon
            icon={faCircleQuestion}
            fade={isFading}
            className={isFading ? classes.iconIsFading : undefined}
          />
        }
        type="blank-default"
        onClick={() => {
          setOpen(true)
        }}
      />
      {isOpen && (
        <Modal
          size="md"
          onCloseRequest={() => {
            setOpen(false)
            setFading(true)
          }}
          title={t('How to Use')}
          footer={
            <Row noBottomPadding vAlignCenter>
              <Col className={classes.doNotShowAgainCheckbox}>
                <Input
                  id="do-not-show-again-checkbox"
                  name="do-not-show-again-checkbox"
                  type="checkbox"
                  label={t('Do not show again')}
                  checked={isCheck}
                  onChange={() => {
                    setCheck(!isCheck)
                    updateSettings({
                      preference__show_annotator_instructions: !settings?.preference__show_annotator_instructions,
                    })
                  }}
                  disabled={inProgress || isFetching}
                />
              </Col>
              <Col>
                <Button
                  onClick={() => {
                    setOpen(false)
                    setFading(true)
                  }}
                  label={t('Close')}
                  type="action"
                  disabled={inProgress || isFetching}
                />
              </Col>
            </Row>
          }
        >
          {annotationTypeToInfoList().map((info) => (
            <p key={uuidv4()}>{info}</p>
          ))}
        </Modal>
      )}
    </>
  )
}

export default AnnotatorInfoModal
